import { Booking, BookingInput, BookingStatus } from '../types/booking';
import { formatBookingRange } from '../utils/slots';

const emailUrl = process.env.REACT_APP_GOOGLE_EMAIL_SYNC_URL ?? '';
const emailSecret = process.env.REACT_APP_GOOGLE_EMAIL_SYNC_SECRET ?? '';
/** CRA espone solo REACT_APP_*; true = non inviare mail */
const skipMail =
  (process.env.REACT_APP_SKIP_MAIL ?? '').trim().toLowerCase() === 'true';

function isConfigured(): boolean {
  return Boolean(emailUrl && emailSecret);
}

function shouldSkipMail(context: string): boolean {
  if (!skipMail) return false;
  console.info(`SKIP_MAIL attivo: saltata ${context}`);
  return true;
}

type EmailResponse = {
  ok?: boolean;
  sent?: string;
  error?: string;
};

function bookingPayload(booking: BookingInput | Booking) {
  return {
    gruppoScout: booking.gruppoScout,
    branca: booking.branca || '—',
    numeroRagazzi: booking.numeroRagazzi,
    numeroAdulti:
      'numeroAdulti' in booking ? booking.numeroAdulti ?? 0 : 0,
    referenteNome: booking.referenteNome,
    telefono: booking.telefono,
    email: booking.email || '—',
    periodo: formatBookingRange(booking),
    startDate: booking.startDate,
    endDate: booking.endDate,
    startTime: booking.startTime,
    endTime: booking.endTime,
    altreInfo: booking.altreInfo?.trim() || '—',
  };
}

function parseEmailResponse(text: string, status: number): EmailResponse {
  try {
    return JSON.parse(text) as EmailResponse;
  } catch {
    throw new Error(
      `Risposta email notify non valida (${status}): ${text.slice(0, 120)}`
    );
  }
}

/**
 * Apps Script Web App: il redirect POST→script.googleusercontent spesso
 * diventa GET e perde il body. Prova POST, poi fallback GET ?payload=
 */
async function postEmail(body: Record<string, unknown>): Promise<void> {
  const postRes = await fetch(emailUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify(body),
    redirect: 'follow',
  });
  const postText = await postRes.text();

  let data: EmailResponse;
  try {
    data = parseEmailResponse(postText, postRes.status);
  } catch {
    const sep = emailUrl.includes('?') ? '&' : '?';
    const getUrl = `${emailUrl}${sep}payload=${encodeURIComponent(
      JSON.stringify(body)
    )}`;
    const getRes = await fetch(getUrl, {
      method: 'GET',
      redirect: 'follow',
    });
    data = parseEmailResponse(await getRes.text(), getRes.status);
  }

  if (!data.ok) {
    throw new Error(data.error || 'Invio email fallito');
  }
}

/** Notifica staff di una nuova richiesta (background, non blocca UX). */
export async function notifyNewBookingRequest(
  booking: BookingInput
): Promise<void> {
  if (shouldSkipMail('notifica staff')) return;
  if (!isConfigured()) {
    console.warn(
      'Email notify non configurato: mancano REACT_APP_GOOGLE_EMAIL_SYNC_* in .env'
    );
    return;
  }

  try {
    await postEmail({
      secret: emailSecret,
      action: 'email_staff',
      ...bookingPayload(booking),
      admin_url: `${window.location.origin}/admin`,
    });
  } catch (err) {
    console.error('Notifica email staff non inviata:', err);
  }
}

/** Notifica il richiedente su approvazione/rifiuto (background). */
export async function notifyBookingStatusToRequester(
  booking: Booking,
  status: Extract<BookingStatus, 'approved' | 'rejected'>
): Promise<void> {
  if (shouldSkipMail('notifica stato richiedente')) return;
  if (!booking.email?.trim()) {
    console.warn('Nessuna email richiedente: skip notifica stato');
    return;
  }
  if (!isConfigured()) {
    console.warn(
      'Email notify non configurato: mancano REACT_APP_GOOGLE_EMAIL_SYNC_* in .env'
    );
    return;
  }

  const approved = status === 'approved';
  try {
    await postEmail({
      secret: emailSecret,
      action: 'email_status',
      ...bookingPayload(booking),
      to: booking.email.trim(),
      status,
      status_label: approved ? 'approvata' : 'non approvata',
      status_title: approved
        ? 'Prenotazione approvata'
        : 'Prenotazione non approvata',
      status_message: approved
        ? 'La tua richiesta di prenotazione della sede è stata approvata.'
        : 'La tua richiesta di prenotazione della sede non è stata approvata.',
      site_url: window.location.origin,
    });
  } catch (err) {
    console.error('Notifica email richiedente non inviata:', err);
  }
}
